"use client";

import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { ChevronRightIcon } from "lucide-react";
import type * as React from "react";

import { cn } from "../cn";
import { CountPill } from "./count-pill";

// Work OS location-header.tsx (design spec 2026-10-20 Work UX pass): the 56 px header of a daily
// screen, two lines: a 20 px breadcrumb (12 px muted, crumbs at most 160 px, ChevronRight between)
// over a 32 px title line (18 px icon, h1 18 / 600, badge, actions on the right). It replaces
// `PageHeader` on screens people open many times a day: no eyebrow, no 28 px title, no subtitle.

type IconType = React.ComponentType<{ className?: string; "aria-hidden"?: boolean }>;

export type LocationCrumb = {
  label: React.ReactNode;
  href?: string;
  /** Router link element for the crumb (e.g. `<Link to=… />`); takes precedence over `href`. */
  render?: React.ReactElement;
};

export type LocationHeaderProps = Omit<React.ComponentProps<"header">, "title"> & {
  crumbs?: LocationCrumb[];
  icon?: IconType;
  title: React.ReactNode;
  /** After the title: a count (`undefined` while it loads), or `badge` for anything else. */
  count?: number | undefined;
  badge?: React.ReactNode;
  /** Right-hand controls (28 px ghost buttons, a primary `size="sm"` last). */
  actions?: React.ReactNode;
  /** Heading level of the title (1 on a page, 2 inside a drawer or panel). */
  titleAs?: "h1" | "h2";
};

function Crumb({ crumb }: { crumb: LocationCrumb }) {
  const el = useRender({
    defaultTagName: crumb.href || crumb.render ? "a" : "span",
    render: crumb.render,
    props: mergeProps<"a">(
      {
        className: cn(
          "max-w-[160px] truncate",
          (crumb.href || crumb.render) && "hover:text-foreground hover:underline",
        ),
        ...(crumb.href ? { href: crumb.href } : {}),
      },
      { children: crumb.label },
    ),
    state: { slot: "location-crumb" },
  });
  return el;
}

export function LocationHeader({
  crumbs,
  icon: Icon,
  title,
  count,
  badge,
  actions,
  titleAs = "h1",
  className,
  ...props
}: LocationHeaderProps) {
  const Heading = titleAs;
  const trail = crumbs?.length ? crumbs : null;
  return (
    <header
      data-slot="location-header"
      className={cn(
        "flex h-14 shrink-0 flex-col justify-center border-b border-subtle px-6 max-md:px-4",
        className,
      )}
      {...props}
    >
      {trail ? (
        <nav
          aria-label="Location"
          data-slot="location-crumbs"
          className="flex h-5 min-w-0 items-center gap-1 text-xs text-muted-foreground"
        >
          {trail.map((c, i) => (
            // Crumbs are positional; the trail never reorders.
            // biome-ignore lint/suspicious/noArrayIndexKey: positional, static per render
            <span key={i} className="flex min-w-0 items-center gap-1">
              {i ? (
                <ChevronRightIcon aria-hidden={true} className="size-3 shrink-0 text-faint" />
              ) : null}
              <Crumb crumb={c} />
            </span>
          ))}
        </nav>
      ) : null}
      {/* On a phone, many actions wrap under the title instead of squeezing it to nothing. */}
      <div className="flex h-8 min-w-0 items-center gap-2 max-md:h-auto max-md:min-h-8 max-md:flex-wrap">
        {Icon ? <Icon aria-hidden={true} className="size-[18px] shrink-0 text-faint" /> : null}
        <Heading className="min-w-0 truncate font-heading text-[18px] leading-7 font-semibold tracking-heading text-foreground">
          {title}
        </Heading>
        {count !== undefined ? <CountPill count={count} /> : null}
        {badge}
        {actions ? (
          <div data-slot="location-actions" className="ml-auto flex shrink-0 items-center gap-1">
            {actions}
          </div>
        ) : null}
      </div>
    </header>
  );
}
